<script type="text/jsx">
import _ from 'lodash';
import { mapGetters } from 'vuex';
import PartnerOfferDetailDialog from '@/views/config/partner-refer/dialog/offer-detail';
import { ServiceConfirm } from '@/components/common/service-dialog/confirm';
import minusIconUrl from '@/assets/partner/minus-logo.svg';
import trackingMixin from '@/views/config/partner-refer/tracking/mx';
import { getMessageTemplateByRules } from '@/views/config/partner-refer/helper/validator';
import { isEmptyVal } from '@/utils';
import { couponFetchStatus } from '@/views/config/coupon/helper/constant';

export default {
  name: 'PartnerReferAlert',
  mixins: [trackingMixin],
  created() {
    this.partnerOfferDetailDialog = new ServiceConfirm(PartnerOfferDetailDialog);
    this.contentNode = null;
  },
  computed: {
    ...mapGetters(['isFromPartnerRefer', 'partnerRefer', 'planMapFlat', 'showInEligibleMsg', 'partnerReferProductNames', 'couponState', 'verifyCouponStatus', 'partnerMessageLoading', 'partnerFeaturesDependOnRules']),
    offerMsg() {
      return _.trimEnd(getMessageTemplateByRules(this.partnerRefer?.offerMsg)?.messageBody);
    },
    generalErrorMsg() {
      return _.trimEnd(getMessageTemplateByRules(this.partnerRefer?.generalErrorMsg)?.messageBody);
    },
    bindCouponRemovedErrorMessage() {
      return _.trimEnd(getMessageTemplateByRules(this.partnerRefer?.couponRemovedGeneralErrorMessage)?.messageBody);
    },
    partnerErrorMessage() {
      const removeCouponMessageSwitch = this.partnerRefer?.rules?.popUpModalForRemoveCoupon;
      const cartCouponCode = this.couponState?.couponCode;

      // need show related remove coupon error message with bind partner
      if (
        removeCouponMessageSwitch
        && this.partnerRefer?.couponCode
        && isEmptyVal(cartCouponCode)
        && this.verifyCouponStatus === couponFetchStatus.idle
        && isEmptyVal(this.couponState?.errorMessage)
      ) {
        return this.bindCouponRemovedErrorMessage;
      }

      return this.generalErrorMsg;
    },
    shouldShowSkeleton() {
      return this.partnerFeaturesDependOnRules?.usePlanCardMode;
    },
  },
  methods: {
    showOfferDetail() {
      this.partnerOfferDetailDialog.confirm();
      this.$tracking.buyInteraction(
        this.mx_trackingPartnerName,
        this.mx_trackingPartnerOfferDetailReason,
      );
    },
    renderEligibleIcon() {
      return <span class="opc-partner-refer__eligible--icon">{this.$t('billing.partner.special_offer')}</span>;
    },
    renderInEligibleIcon() {
      return <img class="opc-partner-refer__ineligible--minus" src={minusIconUrl} />;
    },
    renderOfferDetail() {
      if (this.partnerRefer?.features?.offerDetailBtnFromOpConfig) return null;
      return <zm-button
        type="link"
        class="opc-partner-refer__offer-detail-cta"
        onClick={this.showOfferDetail}>
        {this.$t('billing.partner.offer_detail_cta')}
      </zm-button>;
    },
    renderEligibleTile() {
      return <div class="opc-partner-refer__eligible">
        {this.renderEligibleIcon()}
        <div class="opc-partner-refer__eligible--content">
          <span domPropsInnerHTML={this.offerMsg} />{this.renderOfferDetail()}
        </div>
      </div>;
    },
    renderInEligibleTile() {
      return <div class="opc-partner-refer__ineligible">
        {this.renderInEligibleIcon()}
        <div class="opc-partner-refer__ineligible--content">
          <span role="alert" domPropsInnerHTML={this.partnerErrorMessage} />{this.renderOfferDetail()}
        </div>
      </div>;
    },
    renderEligibleMessage(inEligible) {
      return inEligible ?
        this.renderInEligibleTile() :
        this.renderEligibleTile();
    },
    renderContent() {
      let contentNode = null;

      if (this.partnerMessageLoading) {
        contentNode = this.contentNode;
      } else {
        contentNode = this.contentNode = this.renderEligibleMessage(this.showInEligibleMsg);
      }

      return contentNode ?? this.renderEligibleMessage(this.showInEligibleMsg);
    }
  },
  render() {
    if (!this.isFromPartnerRefer) return;

    if (isEmptyVal(this.offerMsg) && isEmptyVal(this.generalErrorMsg)) return;

    const contentNode = this.renderContent();

    return <div class="opc-partner-refer">
      {
        this.shouldShowSkeleton ?
          <zm-skeleton loading={this.partnerMessageLoading} rows={3}>
            {contentNode}
          </zm-skeleton> :
          contentNode
      }
    </div>;

  },
  watch: {
    showInEligibleMsg: {
      immediate: true,
      handler() {
        const reason = this.mx_trackingPartnerImpressionReason;
        reason && this.$tracking.buyInteraction(this.mx_trackingPartnerName, this.mx_trackingPartnerImpressionReason);
      },
    },
  },
};
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-partner-refer {
  margin-bottom: 16px;

  &__offer-detail-cta {
    &.zm-button--link {
      text-transform: capitalize;
      padding: 0px;
      margin-left: 4px;
    }
  }

  &__eligible, &__ineligible {
    padding: 20px 22px;
    border-radius: 16px;

    &--content {
      font-family: 'Almaden Sans';
      font-weight: 500;
      font-size: 14px;
      line-height: 150%;
      color: #00053D;
      padding-right: 25px;
    }

    @include media(mobile, tablet) {
      padding: 16px;

      &--content {
        padding-right: 16px;
      }
    }
  }

  &__eligible {
    display: flex;
    align-items: center;
    position: relative;
    background: #FFFFFF;
    box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.16);

    &::after {
      font-family: 'icozoom' !important;
      content: "\e940";
      position: absolute;
      border-radius: 8px;
      top: 22px;
      right: 22px;
      color: #0B5CFF;
      line-height: 16px;
      font-size: 16px;
    }

    &--icon {
      background: linear-gradient(96.59deg, #084845 10.36%, #09A639 89.64%);
      font-family: 'SF Pro', 'Almaden Sans';
      font-weight: 400;
      font-size: 12px;
      line-height: 16px;
      padding: 0px 4px;
      color: #ffffff;
      border-radius: 100px;
      white-space: nowrap;
      height: 16px;
      margin-right: 6px;
    }

    &--content {

    }

    @include media(mobile, tablet) {
      &:has(&--icon) {
        flex-wrap: wrap;
      }

      &--icon {
        margin-bottom: 4px;
      }

      &::after {
        top: 38px;
        right: 15px;
      }
    }
  }

  &__ineligible {
    position: relative;
    background: #F2F2F4;

    &--minus {
      position: absolute;
      top: 22px;
      right: 22px;
      width: 16px;
      height: 16px;

      @include media(mobile, tablet) {
        top: 19px;
      }
    }

    &--content {

    }
  }
}

.partner-refer-message-popup {
  .zm-message-box {
    &__title {
      font-size: 24px !important;
      color: #232333;

      span {
        font-family: $happy-display;
        font-weight: 400;
      }
    }
  }

  .zm-button {
    &.zm-button--primary, &.zm-button--plain {
      font-size: 16px;
      line-height: 24px;
      letter-spacing: 0em;
      text-align: center;
    }

    &--plain {
      color: #00053D;
    }
  }
}

</style>
